import { Link, useNavigate } from "react-router-dom";

import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { FaRegEye } from "react-icons/fa6";
import { FaRegEyeSlash } from "react-icons/fa6";
import { object, string } from "yup";
import toast from "react-hot-toast";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setToken } from "../../store/token.slice";
import AuthHeroSection from "./AuthHeroSection";
import AuthLangButton from "../../components/authLangButton/AuthLangButton";
import { setUser } from "@/store/user.slice";
import { setResturant } from "@/store/resturant.slice";
import { useTranslation } from "react-i18next";
import { setWarning } from "@/store/warningSlice";
import { rerenderLayout } from "@/store/RenderProtectionRoute.slice";



export default function Login() {
  const dispatch = useDispatch()
  const passwordRef = useRef<HTMLInputElement>(null)
  const closeEyeRef = useRef<HTMLSpanElement>(null)
  const openEyeRef = useRef<HTMLSpanElement>(null)
  const { t, i18n } = useTranslation();
  const lang = i18n.language
  const [responseError, setResponseError] = useState<string | null>(null)
  const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
  const baseUrl = import.meta.env.VITE_BASE_URL
  const navigate = useNavigate()




  // validationSchema
  const validationSchema = object({
    email: string().email(t("login.enterValidEmail") as string).required(t("login.emailRequired") as string),
    password: string().min(8, t("login.passwordMinLength") as string).required(t("login.passwordRequired") as string).matches(passwordRegex, t("login.passwordComplexity") as string),
  })


  useEffect(() => {
  const getQueryParamToken = async () => {
    
      const url = window.location.href;
      const searchParams = new URLSearchParams(new URL(url).search);

      const searchToken = searchParams.get("token");
      const subscriptionRedirect = searchParams.get("subscription-redirect");

      if (searchToken && subscriptionRedirect === "true") {
        dispatch(setToken(searchToken));
        dispatch(rerenderLayout())
        navigate("/settings/subscribtion");
      } 
    }
  

  getQueryParamToken();
}, [dispatch, navigate]);


  // login function 
  async function loginUser() {
    const toastId = toast.loading(t("login.loggingIn") as string)
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/users/login?locale=${lang}`,
        method: "POST",
        data: {
          email: formik.values.email,
          password: formik.values.password
        }
      }
      const response = await axios.request(options)
      if (response.data.success == false) {
        setResponseError(t("login.invalidCredentials") as string)
      } else if (response.data.success == true) {
        if (!response.data.activateToken) {
          dispatch(setToken(response.data.accessToken));
          dispatch(setUser(response.data.userData));
          dispatch(setResturant(response.data.restaurantData));
          dispatch(setWarning({ show: true }));
          navigate("/")

          toast.success(t("login.loginSuccess") as string)
        } else {
          dispatch(setToken(response.data.activateToken));
          navigate("/activate-account")

        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data.error)
      } else {
        toast.error(t("login.unexpectedError") as string)
      }

    } finally {
      toast.dismiss(toastId)
    }
  }

  const isRTL = i18n.language === "ar";
  // controlled inputs
  const formik = useFormik({
    initialValues: {
      email:  "",
      password:  "",
    },
    validationSchema,
    onSubmit: loginUser
  })
  // passwordvisabilty function
  function togglePasswordVisibility() {
    if (passwordRef.current) {
      if (passwordRef.current.type === "password") {
        passwordRef.current.type = "text"
        openEyeRef.current?.classList.remove("hidden")
        closeEyeRef.current?.classList.add("hidden")

      } else {
        passwordRef.current.type = "password"
        openEyeRef.current?.classList.add("hidden")
        closeEyeRef.current?.classList.remove("hidden")
      }
    }
  }



  return (
    <>
      <div className="xl:grid xl:grid-cols-12">

        <form
          onSubmit={formik.handleSubmit}
          className=" h-[75vh] relative mt-10 flex justify-between w-[90%] md:w-[60%] xl:w-[70%] mx-auto flex-col col-span-4 "
        >
          <AuthLangButton />

          <div className="flex flex-col gap-5 w-full  p-5 ">

            <h1 className="my-2  text-xl font-semibold flex flex-col gap-2">
              {t("login.welcomeMessage")} 👋🏻
              <span className="text-sm text-slate-400 whitespace-nowrap">{t("login.welcome")}</span>
            </h1>

            <div className="flex w-full  flex-col gap-3">
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="font-semibold">{t("login.email")}</label>
                <input
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  name="email"
                  type="email"
                  className="input"
                  placeholder={t("login.emailPlaceholder") as string}
                />
                {formik.errors.email && formik.touched.email && <span className="text-red-500 text-sm">{formik.errors.email}</span>}
              </div>

              <div className="flex flex-col gap-2 relative">
                <label htmlFor="" className="font-semibold">{t("login.password")}</label>
                <input
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  ref={passwordRef}
                  name="password"
                  type="password"
                  className="input "
                  placeholder={t("login.passwordPlaceholder") as string}
                />
                <span
                  ref={openEyeRef}
                  className={`absolute hidden ${isRTL ? "left-2" : "right-2"} bottom-3 text-slate-400 cursor-pointer`}
                  onClick={togglePasswordVisibility}
                >
                  <FaRegEye />
                </span>

                <span
                  ref={closeEyeRef}
                  className={`absolute ${isRTL ? "left-2" : "right-2"} bottom-3 text-slate-400 cursor-pointer`}
                  onClick={togglePasswordVisibility}
                >
                  <FaRegEyeSlash />
                </span>

              </div>
              {formik.errors.password && formik.touched.password && <span className="text-red-500 text-sm">{formik.errors.password}</span>}
              {responseError && <span className="text-red-500 text-sm">{responseError}</span>}
              <div className="flex justify-between gap-3 items-center">

                <Link to={'/forget-password'} className="text-sidebar whitespace-nowrap">
                  {t("login.forgotPassword")}
                </Link>
              </div>

              <button type="submit" className="my-btn py-4">
                {t("login.login")}
              </button>

              <div className="flex justify-center items-center gap-5">
                <span>  {t("login.noAccount")}</span>
                <Link to={'/register'} className=" text-sidebar">
                  {t("login.signup")}
                </Link>
              </div>
            </div>
          </div>
        </form>
        <AuthHeroSection />
      </div>
    </>
  )
}

